import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import { useRecaptcha } from '../hooks/useRecaptcha';
import Spinner from './Spinner';
import puzzlePlanetLogo from '../images/puzzle_planet_title.webp';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const {
        isLoaded,
        isLoading: recaptchaLoading,
        error: recaptchaError,
        getToken,
    } = useRecaptcha();

    // Extract invite code from URL query string
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const codeFromUrl = urlParams.get('inviteCode');
        if (codeFromUrl) {
            setInviteCode(codeFromUrl);
        }
    }, [location.search]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Get reCAPTCHA token
            const recaptchaToken = await getToken('login');

            const response = await authApi.login({ email, password, recaptchaToken });

            if (response.status === 'success') {
                // Successful login
                console.log('Login successful:', response);

                // If there's an invite code, accept the invite first
                if (inviteCode) {
                    try {
                        const acceptResponse = await authApi.acceptInvite({ inviteCode });
                        if (acceptResponse.status === 'success') {
                            // Redirect to game page with gameId
                            const gameUrl = new URL(import.meta.env.VITE_REACT_APP_GAME_URL);
                            gameUrl.searchParams.set('t', new Date().getTime().toString());
                            if (acceptResponse.gameId) {
                                gameUrl.searchParams.set('gameId', acceptResponse.gameId);
                            }
                            window.location.href = gameUrl.toString();
                            return;
                        } else {
                            setError(acceptResponse.message || 'Failed to accept invite');
                            return;
                        }
                    } catch (inviteErr) {
                        setError('Failed to accept invite. Please try again.');
                        return;
                    }
                }

                // No invite code, redirect to game page normally
                window.location.href = `${
                    import.meta.env.VITE_REACT_APP_GAME_URL
                }?t=${new Date().getTime()}`;
            } else if (response.status === 'challenge') {
                if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
                    // Store session and email for password setting
                    sessionStorage.setItem('challengeSession', response.session || '');
                    sessionStorage.setItem('challengeEmail', email);
                    navigate('/set-password');
                } else {
                    setError(`Authentication challenge: ${response.challengeName}`);
                }
            } else {
                setError(response.message || 'Login failed');
            }
        } catch (err) {
            if (err instanceof Error && err.message.includes('reCAPTCHA')) {
                setError('reCAPTCHA verification failed. Please try again.');
            } else {
                setError('An unexpected error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
            style={{ backgroundColor: '#1D1A31' }}
        >
            <div className='max-w-md w-full space-y-8'>
                {/* Header */}
                <div className='text-center'>
                    <img
                        src={puzzlePlanetLogo}
                        alt='Puzzle Planet'
                        className='mx-auto mb-4 max-w-xs'
                    />
                    <p className='text-lg text-white'>
                        Welcome back! Please sign in to your account.
                    </p>
                </div>

                {/* Login Form */}
                <div className='bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100'>
                    <form onSubmit={handleSubmit} className='space-y-6'>
                        {/* Email Field */}
                        <div>
                            <label
                                htmlFor='email'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Email Address
                            </label>
                            <input
                                type='email'
                                id='email'
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                disabled={loading}
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                placeholder='Enter your email'
                            />
                        </div>

                        {/* Password Field */}
                        <div>
                            <label
                                htmlFor='password'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Password
                            </label>
                            <input
                                type='password'
                                id='password'
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                disabled={loading}
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                placeholder='Enter your password'
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                                {error}
                            </div>
                        )}

                        {/* reCAPTCHA Error Message */}
                        {recaptchaError && (
                            <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                                reCAPTCHA Error: {recaptchaError}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type='submit'
                            disabled={loading || recaptchaLoading || !isLoaded}
                            className='w-full bg-gradient-to-b from-yellow-400 to-orange-500 text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                        >
                            {loading ? (
                                <div className='flex items-center justify-center'>
                                    <Spinner size='md' color='black' className='-ml-1 mr-3' />
                                    Signing in...
                                </div>
                            ) : recaptchaLoading ? (
                                'Loading reCAPTCHA...'
                            ) : (
                                'Sign In'
                            )}
                        </button>
                    </form>

                    {/* Forgot Password Link */}
                    <div className='mt-6 text-center'>
                        <button
                            type='button'
                            onClick={() => navigate('/forgot-password')}
                            disabled={loading}
                            className='text-blue-500 hover:text-blue-600 font-medium text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            Forgot your password?
                        </button>
                    </div>
                </div>

                {/* Footer */}
                {import.meta.env.VITE_STRIPE_SIGNUPS_ENABLED === 'true' ? (
                    <div className='text-center'>
                        <p className='text-sm text-gray-500'>
                            Don't have an account?{' '}
                            <button
                                type='button'
                                onClick={() => navigate('/signup-new')}
                                className='text-green-500 hover:text-green-600 font-medium transition-colors duration-200'
                            >
                                Sign up here
                            </button>
                        </p>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default Login;
